/* eslint-disable no-console */
import { jwtDecode } from 'jwt-decode';

import { userApi } from 'api';

export const INTERCOM_JWT_TOKEN_KEY = 'intercom_jwt_token';

const TOKEN_REFRESH_INTERVAL = 585000; // 9 minutes 45 seconds

let refreshInterval = null;

/**
 * Gets the Intercom JWT from session storage.
 * @returns {string | null} The Intercom JWT token, or null if not found or sessionStorage is inaccessible.
 */
const getIntercomJwt = () => {
  try {
    return sessionStorage.getItem(INTERCOM_JWT_TOKEN_KEY);
  } catch (error) {
    console.warn('Failed to access sessionStorage:', error);
    return null;
  }
};

/**
 * Sets the Intercom JWT in session storage and updates the Intercom instance.
 * @param {string} intercomToken - The Intercom JWT to be stored.
 * @returns {void}
 */
const setIntercomJwt = (intercomToken) => {
  try {
    if (!intercomToken || typeof intercomToken !== 'string') {
      throw new Error('Invalid token format');
    }

    if (typeof Storage === 'undefined') {
      throw new Error('SessionStorage not supported');
    }

    sessionStorage.setItem(INTERCOM_JWT_TOKEN_KEY, intercomToken);

    if (window?.Intercom) {
      window.Intercom('update', {
        intercom_user_jwt: intercomToken,
      });
    }
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn('SessionStorage quota exceeded');
    } else {
      console.warn('Failed to store token:', error);
    }
  }
};

/**
 * Removes the Intercom JWT from session storage.
 * @returns {void}
 */
const removeIntercomJwt = () => {
  try {
    sessionStorage.removeItem(INTERCOM_JWT_TOKEN_KEY);
  } catch (error) {
    console.warn('Failed to remove token:', error);
  }
};

/**
 * Checks if the stored Intercom JWT is valid and matches the current user.
 * @param {object} user - The current user object.
 * @param {string} user.uuid - The user's unique identifier.
 * @param {string} user.email - The user's email address.
 * @returns {boolean} True if the token is valid and matches the user, false otherwise.
 */
const checkIntercomValidity = (user) => {
  const token = getIntercomJwt();
  if (!token) return false;
  try {
    const decodedToken = jwtDecode(token);
    if (decodedToken?.exp < Date.now() / 1000) return false;
    return (
      user.uuid === decodedToken?.user_id && user.email === decodedToken?.email
    );
  } catch (error) {
    return false;
  }
};

/**
 * Fetches a new Intercom JWT from the API, stores it, and returns it.
 * @async
 * @returns {Promise<string>} A promise that resolves with the new Intercom JWT.
 */
async function generateIntercomJwt() {
  const {
    intercomJwt: { token: intercomJwt },
  } = await userApi.getIntercomJwt();
  setIntercomJwt(intercomJwt);
  return intercomJwt;
}

/**
 * Starts a recurring interval to refresh the Intercom JWT before it expires.
 * @returns {void}
 */
const startIntercomJwtRefresh = () => {
  if (refreshInterval) clearInterval(refreshInterval);
  refreshInterval = setInterval(async () => {
    try {
      await generateIntercomJwt();
    } catch (error) {
      console.warn('Failed to refresh Intercom token:', error);
    }
  }, TOKEN_REFRESH_INTERVAL);
};

/**
 * Stops the recurring interval for refreshing the Intercom JWT.
 * @returns {void}
 */
const stopIntercomJwtRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

/**
 * Gets a valid Intercom token for the current user. If the existing token is invalid or missing,
 * it generates a new one and starts the refresh interval.
 * @async
 * @param {object | null} user - The current user object.
 * @param {string} user.uuid - The user's unique identifier.
 * @param {string} user.email - The user's email address.
 * @param {Function} intercomShutdown - A function to call to shut down Intercom if the token is invalid.
 * @returns {Promise<string | null>} A promise that resolves with a valid Intercom JWT, or null if no user is provided.
 */
export const getIntercomTokenForCurrentUser = async (
  user,
  intercomShutdown,
) => {
  if (!user) return null;
  const isTokenValid = checkIntercomValidity(user);
  if (!isTokenValid) {
    if (window?.Intercom) {
      intercomShutdown();
    }
    const token = await generateIntercomJwt();
    startIntercomJwtRefresh();
    return token;
  }
  const token = getIntercomJwt();
  if (token) startIntercomJwtRefresh();
  return token;
};

/**
 * Clears all Intercom JWT data, including the token from session storage
 * and the refresh interval timer.
 * @returns {void}
 */
export const clearIntercomJwt = () => {
  stopIntercomJwtRefresh();
  removeIntercomJwt();
};
